// The docked minimap (desktop rail slot / phone drawer). Extracted from
// App.tsx (ROADMAP 7.14); renders in the same DOM position.

import { Minimap } from '@aod/ui';
import type { CSSProperties, ComponentProps } from 'react';
import type { MobileHudPanel } from '../components/MobileHudToggles.js';
import type { SimWorld } from '../sim/useSimWorld/index.js';
import { PanelResizeHandle } from '../ui/layout/PanelDockControls.js';
import type { useMinimapWiring } from './useMinimapWiring.js';

type MinimapProps = ComponentProps<typeof Minimap>;

interface MatchMinimapDockProps {
  sim: SimWorld;
  mobilePanel: MobileHudPanel;
  dockStyle: CSSProperties | undefined;
  markers: MinimapProps['markers'];
  extras: Partial<MinimapProps>;
  wiring: ReturnType<typeof useMinimapWiring>;
}

export function MatchMinimapDock({
  sim,
  mobilePanel,
  dockStyle,
  markers,
  extras,
  wiring,
}: MatchMinimapDockProps) {
  const {
    handleMinimapClick,
    handleMinimapCameraDrag,
    handleMinimapCameraDragEnd,
    cameraPolygonPoints,
    cameraFocus,
    particleEffects,
    minimapViewBox,
    setMinimapViewBox,
    pings,
  } = wiring;
  if (!sim.minimapSnapshot) return null;
  return (
    <div
      style={dockStyle}
      className={`absolute right-3 bottom-16 z-30 w-[calc(100vw-1.5rem)] md:bottom-4 md:w-(--rail-w) ${
        mobilePanel === 'map'
          ? // Phone: the minimap drawer (spec §6). It sits ABOVE the
            // mobile HUD toggle bar (which is 44 px tall + padding at
            // bottom-3), so the two never overlap. The height comes from
            // the Minimap component itself; here we only place it.
            'max-md:inset-x-3 max-md:bottom-[4.75rem] max-md:top-auto max-md:w-auto'
          : 'max-md:hidden'
      }`}
    >
      {/* Dock placement wrapper: the minimap fills it; its own collapse
          and toolbar stay as they were. */}
      <Minimap
        snapshot={sim.minimapSnapshot}
        markers={markers}
        {...extras}
        onTileClick={handleMinimapClick}
        onCameraDrag={handleMinimapCameraDrag}
        onCameraDragEnd={handleMinimapCameraDragEnd}
        cameraPolygon={cameraPolygonPoints}
        showCameraBox={minimapViewBox}
        onToggleCameraBox={() => setMinimapViewBox(!minimapViewBox)}
        cameraFocus={cameraFocus}
        pings={pings}
        animatePings={particleEffects}
        className="w-full"
      />
      <PanelResizeHandle id="minimap" side="left" />
    </div>
  );
}
